import { useCallback, useEffect, useRef } from 'react';
import { driver, type DriveStep } from 'driver.js';
import type { TourStep, DashboardTourOptions } from './types';

/**
 * کلاس‌های انیمیشنِ مختصِ حالتِ لودینگ (اسپینر/اسکلتونِ LoadingSpinner).
 * تا وقتی هر عنصری با این کلاس‌ها روی صفحه باشد، یعنی هنوز در حال بارگذاری است و
 * تورِ خودکار نباید شروع شود.
 */
const LOADER_SELECTOR = '.animate-loading-spin, .animate-loading-pulse, .animate-shimmer';

/**
 * پیکربندیِ مشترکِ driver.js را می‌سازد و قدم‌ها را اجرا می‌کند.
 * تمِ RTL/فارسی از طریق کلاسِ `driver-rtl` در `styles/driver-tour.css` اعمال می‌شود.
 */
function runTour(steps: TourStep[]): void {
  // فقط قدم‌هایی که المانشان در DOM موجود است (یا قدمِ بدون‌المان) نگه داشته می‌شوند،
  // تا اگر کامپوننتی روی صفحه نباشد (شرط/لایسنس) قدمش خودکار حذف شود.
  const present = steps.filter((s) => !s.element || document.querySelector(s.element));
  if (!present.length) return;

  const driveSteps: DriveStep[] = present.map((s) => ({
    element: s.element,
    onHighlightStarted: s.centerOnScroll
      ? (element) =>
          element?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' })
      : undefined,
    popover: {
      title: s.title,
      description: s.description,
      side: s.side,
      align: s.align,
    },
  }));

  driver({
    showProgress: true,
    allowClose: true,
    animate: true,
    // smoothScroll عمداً خاموش است: داشبوردها داخل کانتینرِ overflow-y-auto اسکرول
    // می‌شوند و driver فقط به scrollِ window گوش می‌دهد؛ اسکرولِ آنی پایدار است و
    // نرمیِ حرکت از طریق CSS transitionِ پاپ‌اور تأمین می‌شود.
    overlayColor: 'rgba(0, 0, 0, 0.6)',
    stagePadding: 6,
    stageRadius: 10,
    popoverClass: 'driver-rtl',
    nextBtnText: 'بعدی',
    prevBtnText: 'قبلی',
    doneBtnText: 'پایان',
    progressText: '{{current}} از {{total}}',
    steps: driveSteps,
  }).drive();
}

/**
 * هوکِ مشترکِ تورِ داشبورد.
 *
 * @param steps  آرایهٔ قدم‌ها. **باید ثابت باشد** (در سطحِ ماژول تعریف شود، نه inline)
 *               تا اجرای خودکار در هر render تکرار نشود.
 * @param options storageKey یکتا + autoStart + readySelector اختیاری.
 *
 * @example
 *   const { startTour } = useDashboardTour(EVALUATION_GROWTH_TOUR, {
 *     storageKey: 'tour:evaluation-growth',
 *   });
 */
export function useDashboardTour(
  steps: TourStep[],
  options: DashboardTourOptions
): { startTour: () => void } {
  const { storageKey, autoStart = true, readySelector } = options;
  const autoStartedRef = useRef(false);

  const startTour = useCallback(() => {
    runTour(steps);
  }, [steps]);

  // اجرای خودکارِ یک‌بار در اولین بازدید. چون دادهٔ داشبورد async لود می‌شود، صبر می‌کنیم تا
  // (۱) المانِ هدف رندر شده باشد و (۲) هیچ اسپینر/اسکلتونِ لودینگ روی صفحه نمانده باشد؛
  // یعنی کامپوننت‌ها کاملاً لود شده‌اند. سپس تور را اجرا و فلگِ «دیده‌شده» را ذخیره می‌کنیم.
  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    try {
      if (localStorage.getItem(storageKey)) return;
    } catch {
      return; // localStorage در دسترس نیست → خودکار اجرا نکن
    }

    const ready = readySelector ?? steps.find((s) => s.element)?.element;

    const begin = () => {
      autoStartedRef.current = true;
      try {
        localStorage.setItem(storageKey, '1');
      } catch {
        /* بی‌اهمیت */
      }
      startTour();
    };

    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      const targetReady = !ready || !!document.querySelector(ready);
      const stillLoading = !!document.querySelector(LOADER_SELECTOR);

      if (targetReady && !stillLoading) {
        // کامپوننت‌ها لود شده‌اند → تور را نشان بده.
        window.clearInterval(timer);
        begin();
      } else if (tries >= 30) {
        // fallback پس از ~۱۵ ثانیه: اگر هدف موجود است، با وجودِ یک loaderِ احتمالاً
        // بی‌ربط هم شروع کن تا تور برای همیشه بلوکه نشود.
        window.clearInterval(timer);
        if (targetReady) begin();
      }
    }, 500);

    return () => window.clearInterval(timer);
  }, [autoStart, storageKey, readySelector, steps, startTour]);

  return { startTour };
}
